import { Injectable } from '@angular/core';
import {
    TimeSeries,
    TimeSeriesWithHistory,
} from '@clean-code/models';
import { DxPivotGridTypes } from 'devextreme-angular/ui/pivot-grid';
import { CellClickEvent } from 'devextreme/ui/pivot_grid';
import { Field } from 'devextreme/ui/pivot_grid/data_source';

export interface NullCell {
  tradeDate: string;
  identifier: string;
  nullFields: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PivotTableFunctionsService {
  findMatch(
    position: { columnSymbol: string; rowDate: string },
    source:
      | TimeSeries[]
      | TimeSeriesWithHistory[]
      | { tradeDate: Date; identifier: string }[],
  ) {
    return source.find(
      (f: any) =>
        f.identifier === position.columnSymbol &&
        f.tradeDate.toLocaleDateString('en-CA') === position.rowDate,
    );
  }

  cellStyle(
    event: DxPivotGridTypes.CellPreparedEvent,
    backgroundColor: string,
    textColor: string,
  ) {
    event.cellElement.style.backgroundColor = backgroundColor;
    event.cellElement.style.color = textColor;
  }

  cellValues(
    event: DxPivotGridTypes.CellPreparedEvent,
    value: string | number | null,
    text: string,
    innerText: string,
  ) {
    event.cell.value = value;
    event.cell.text = text;
    event.cellElement.innerText = innerText;
  }

  cellPosition(event: CellClickEvent): {
    columnSymbol: string;
    rowDate: string;
    unit: string;
  } {
    const columnPath = event.cell.columnPath[0].toString().split(',');
    let columnSymbol = '';
    let columnUnit = '';

    // symbolSelector method in PivotTableConfigService can return "name, identifier, unit" or "identifier, unit"
    if (columnPath.length === 2) {
      columnSymbol = columnPath[0];
      columnUnit = columnPath[1].trim();
    } else if (columnPath.length === 3) {
      columnSymbol = columnPath[1].trim();
      columnUnit = columnPath[2].trim();
    }
    const row = event.cell.rowPath[0];
    return {
      columnSymbol: columnSymbol,
      rowDate: new Date(row).toLocaleDateString('en-CA'), // en-CA gives YYYY-MM-DD format
      unit: columnUnit,
    };
  }

  columnHelper(columns: Array<Field>, dataIndex: number): string {
    const c = columns.find((f) => f.areaIndex === dataIndex);
    return c.dataField;
  }

  setNullCells(source: TimeSeries[]): NullCell[] {
    const result: NullCell[] = [];

    source.forEach((item) => {
      const nullFields: Array<
        'openPrice' | 'highPrice' | 'lowPrice' | 'volume' | 'settlementPrice'
      > = [];

      if (item.openPrice === null) {
        nullFields.push('openPrice');
      }
      if (item.highPrice === null) {
        nullFields.push('highPrice');
      }
      if (item.lowPrice === null) {
        nullFields.push('lowPrice');
      }
      if (item.volume === null) {
        nullFields.push('volume');
      }
      if (item.settlementPrice === null) {
        nullFields.push('settlementPrice');
      }

      if (nullFields.length > 0) {
        result.push({
          tradeDate: new Date(item.tradeDate).toLocaleDateString('en-CA'),
          identifier: item.identifier,
          nullFields,
        });
      }
    });

    return result;
  }

  columnNameToTranslationKey(columnName: string) {
    const columnNameLower = columnName.toLowerCase();
    switch (columnNameLower) {
      case 'openprice':
        return 'OPEN_PRICE';
      case 'highprice':
        return 'HIGH_PRICE';
      case 'lowprice':
        return 'LOW_PRICE';
      case 'volume':
        return 'VOLUME';
      case 'settlementprice':
        return 'SETTLEMENT_PRICE';
    }
    return 'ERROR';
  }
}
